import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { applyTheme, type CornerStyle, type ThemeName } from './themes';

/* ============================================================
   Organization appearance.

   The Super Admin chooses ONE theme + corner style for the whole
   org (Admin → Appearance). It applies to every user. The mock
   persists the choice to localStorage; in production this is a row
   in a `settings` table written by the superadmin and read at boot.
   ============================================================ */

const THEME_KEY = 'wped.theme';
const CORNERS_KEY = 'wped.corners';

interface AppearanceState {
  theme: ThemeName;
  corners: CornerStyle;
  setTheme: (t: ThemeName) => void;
  setCorners: (c: CornerStyle) => void;
}

const ThemeCtx = createContext<AppearanceState | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(
    () => (localStorage.getItem(THEME_KEY) as ThemeName) ?? 'Cozy',
  );
  const [corners, setCornersState] = useState<CornerStyle>(
    () => (localStorage.getItem(CORNERS_KEY) as CornerStyle) ?? 'Soft',
  );

  // Apply before paint so the first frame is correct.
  useLayoutEffect(() => {
    applyTheme(theme, corners);
  }, [theme, corners]);

  const value = useMemo<AppearanceState>(
    () => ({
      theme,
      corners,
      setTheme: (t) => {
        setThemeState(t);
        localStorage.setItem(THEME_KEY, t);
      },
      setCorners: (c) => {
        setCornersState(c);
        localStorage.setItem(CORNERS_KEY, c);
      },
    }),
    [theme, corners],
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): AppearanceState {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
